import { useState, useMemo } from 'react';
import {
  ListboxStyled, ListboxStyledButton, ListboxStyledOptions, ListboxStyledOption, Button,
} from '@components/common';
import { RadioCheckedIcon } from '@components/common/Icons';
import { ChevronDownIcon, XMarkIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

/** Props */
interface JobLanguagePillProps {
  languageItem?: ApiSchema<'SpokenLanguageWrite2'>;
  languageLevels?: ApiSchema<'OptionItem'>[];
  languagesList?: ApiSchema<'OptionItem'>[];
  changeLanguageLevel: (languageShortName?: string, languageLevel?: string) => void;
  deleteLanguage: (languageShortName?: string) => void;
}

/** Helper tailwind classes */
const btn = {
  radio: 'invisible size-5 ui-selected:visible ui-selected:text-primary-light',
  option: 'flex items-center gap-2 w-full px-3 py-2 text-left text-ink-medium hover:bg-primary hover:text-white ui-selected:text-ink ui-selected:font-bold text-sm',
  delete: '-mr-1.5 ml-2.5 flex size-6 items-center justify-center rounded-full bg-surface-100 text-ink/70 transition-all duration-200 hover:bg-error hover:text-surface',
};

/** @description The language pill component.
 * * **language** - the language object.
 * * **languageLevels** - the language levels list.
 * * **changeLanguageLevel** - the function to change the language level.
 * * **deleteLanguage** - the function for deleting a language from the 'selected' list.
 */
export const JobLanguagePill = (props: JobLanguagePillProps) => {
  // Destructure props
  const {
    languageItem,
    languageLevels,
    languagesList,
    changeLanguageLevel,
    deleteLanguage,
  } = props;

  // Get the initial language level object
  const initialLevelObj = useMemo(
    () => languageLevels?.find((level) => level.id === String(languageItem?.level)),
    [languageLevels, languageItem],
  );

  // Get the language name
  const language = useMemo(
    () => languagesList?.find((lang) => lang.id === languageItem?.shortName),
    [languagesList, languageItem],
  );

  // Store the listbox selected language level object
  const [
    selectedLanguageLevel, setSelectedLanguageLevel,
  ] = useState<ApiSchema<'OptionItem'> | undefined>(initialLevelObj);

  // Return nothing if there is no relevant data
  if (!languageItem) return null;

  // Render component
  // ********************
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border border-input px-3 py-1 text-sm">
      {/* Icon */}
      <GlobeAltIcon className="size-4.5 text-ink-medium" />

      {/* Language name */}
      <span className="font-semibold">{`${language?.name} -`}</span>

      {/* Language levels dropdown */}
      <ListboxStyled
        value={selectedLanguageLevel}
        onChange={setSelectedLanguageLevel}
      >
        <ListboxStyledButton
          as={Button}
          styling="text"
          color="primary-light"
          className="text-sm outline-0"
        >
          {selectedLanguageLevel?.name}
          <ChevronDownIcon className="ml-1.5 size-4 stroke-2" />
        </ListboxStyledButton>

        {/* Language levels options */}
        <ListboxStyledOptions
          className="mt-1 min-w-40 divide-y overflow-hidden !rounded-md"
          animation="slideDown"
        >
          {languageLevels && languageLevels.map((level) => (
            <ListboxStyledOption
              key={`option-${level?.id || level?.name}`}
              value={level}
            >
              <Button
                styling="none"
                className={btn.option}
                onClick={() => changeLanguageLevel(languageItem?.shortName, level?.id)}
              >
                <RadioCheckedIcon className={btn.radio} />
                {level?.name}
              </Button>
            </ListboxStyledOption>
          ))}
        </ListboxStyledOptions>
      </ListboxStyled>

      {/* Remove language button */}
      <Button
        styling="none"
        className={btn.delete}
        onClick={() => deleteLanguage(languageItem?.shortName)}
      >
        <XMarkIcon className="size-3 stroke-3" />
      </Button>
    </div>
  );
};
